import { useCallback, useEffect, useRef, useState } from "react";

export type LoadStatus = "empty" | "error" | "loading" | "ready";

export type AdminMetric = {
  detail: string;
  label: string;
  tone: "critical" | "info" | "success" | "warning";
  value: string;
};

export type AdminReview = {
  body: string;
  conversationStatus?: string | null;
  conversationStep?: string | null;
  conversationWindowClosed?: boolean;
  conversationWindowEndsAt?: string | null;
  country?: string;
  createdAt: string;
  customerReplies: Array<{ receivedAt: string; text: string }>;
  displayName: string;
  featured: boolean;
  id: string;
  language?: string;
  lastCustomerReplyAt?: string | null;
  media: string;
  mediaCount: number;
  mediaPreviewUrl?: string | null;
  merchantReply?: string | null;
  product: string;
  productHandle?: string;
  rating: number;
  rawSource: string;
  rawStatus: string;
  risk: string;
  source: string;
  status: string;
  title: string | null;
  verified: boolean;
};

export type AdminReviewRequest = {
  channel: string;
  customer: string;
  due: string;
  expires: string;
  fallback: string;
  id: string;
  itemCount: number;
  order: string;
  products: string;
  rawStatus: string;
  reminderCount: number;
  status: string;
  trigger?: string;
};

export type AdminOrderCandidate = {
  currency: string;
  customer: string;
  deliveredAt: string | null;
  deliveredAgeDays: number | null;
  eligible: boolean;
  eligibilityLabel: string;
  eligibilityReason: string;
  fulfilledAt: string | null;
  id: string;
  itemCount: number;
  manualEligible: boolean;
  manualEligibilityLabel: string;
  manualEligibilityReason: string;
  orderNumber: string;
  processedAt: string;
  products: string;
  request: {
    channel: string;
    due: string;
    id: string;
    rawStatus: string;
    status: string;
  } | null;
  readyAt: string | null;
  total: number;
};

export type SettingsField = {
  defaultValue: boolean | number | string;
  help: string;
  label: string;
  name: string;
  options?: string[];
  type: "boolean" | "color" | "number" | "select" | "text";
};

export type SettingsGroup = {
  description: string;
  fields: SettingsField[];
  key: string;
  label: string;
  updatedAt: string;
  values: Record<string, boolean | number | string>;
};

export type WhatsAppCredentialState = {
  configured: boolean;
  label: string;
  source: "database" | "environment" | "missing";
  value: string;
};

export type WhatsAppTemplate = {
  body: string;
  buttons?: Array<{ text: string; type: string }>;
  category: string;
  createdAt?: string;
  id: string;
  language: string;
  name: string;
  source: "gupshup" | "local";
  status: string;
  type: string;
};

export type WhatsAppConsole = {
  config: {
    credentials: Record<string, WhatsAppCredentialState>;
    required: Array<{ configured: boolean; key: string; label: string }>;
    webhookUrl: string;
  };
  ok: true;
  recentMessages: Array<{
    id: string;
    lastError: string | null;
    providerMessageId: string;
    queuedAt: string;
    rawStatus: string;
    sentAt: string | null;
    status: string;
    templateName: string;
  }>;
  settings: {
    enabled: boolean;
    maxReminders: number;
    quietHoursEnd: string;
    quietHoursStart: string;
    reminderTemplateName: string;
    templateName: string;
    templateType: string;
  };
  status: {
    lastMessageAt: string | null;
    lastWebhookAt: string | null;
    sendReady: boolean;
    templateSyncReady: boolean;
  };
  templates: WhatsAppTemplate[];
  webhookEvents: Array<{
    eventType: string;
    providerEventId: string;
    providerMessageId: string;
    rawStatus: string;
    receivedAt: string;
    status: string;
  }>;
};

export type ImportBatch = {
  confirmedAt?: string;
  createdAt: string;
  errorCount: number;
  filename: string;
  id: string;
  rowCount: number;
  rows: Array<{
    errors: string[];
    id: string;
    mapped: Record<string, unknown>;
    rowNumber: number;
    status: string;
  }>;
  status: string;
  validCount: number;
};

type QueryState<T> = {
  data?: T;
  error?: string;
  reload: () => Promise<void>;
  status: LoadStatus;
};

type ShopifyGlobalWithIdToken = {
  idToken?: () => Promise<string>;
  ready?: Promise<void>;
};

type AdminErrorContext = {
  adminAppUrl?: string;
  authUrl?: string;
};

type AdminErrorPayload = {
  context?: AdminErrorContext;
  code?: string;
  message?: string;
};

export class AdminApiError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly context: AdminErrorContext = {},
  ) {
    super(message);
  }
}

declare global {
  interface Window {
    shopify?: ShopifyGlobalWithIdToken;
  }
}

const apiBase =
  typeof window === "undefined"
    ? ""
    : ((window as Window & { __WHATSTAR_ADMIN_API_BASE__?: string }).__WHATSTAR_ADMIN_API_BASE__ ?? "");

export async function adminFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await adminResponse(path, options);
  const payload = await parseResponsePayload(response);
  if (!response.ok) throw buildAdminApiError(payload, response.status, response.headers);
  if (typeof payload === "string") {
    throw new AdminApiError("Admin API returned a non-JSON response.", "ADMIN_API_INVALID_RESPONSE");
  }
  return payload as T;
}

export async function adminDownload(path: string, filename: string) {
  const response = await adminResponse(path);
  if (!response.ok) {
    const payload = await parseResponsePayload(response);
    throw buildAdminApiError(payload, response.status, response.headers);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

async function adminResponse(path: string, options: RequestInit = {}) {
  const headers = new Headers(options.headers);
  if (options.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");

  const token = await getShopifyIdToken();
  if (token && !headers.has("Authorization")) headers.set("Authorization", `Bearer ${token}`);

  return fetch(`${apiBase}${withShopifyContext(path)}`, {
    ...options,
    headers,
  });
}

async function parseResponsePayload(response: Response) {
  const contentType = response.headers.get("content-type") ?? "";
  return contentType.includes("application/json") ? response.json() : response.text();
}

export function adminPost<T>(path: string, body: unknown) {
  return adminFetch<T>(path, {
    body: JSON.stringify(body),
    method: "POST",
  });
}

export function useAdminQuery<T>(path: string, isEmpty?: (data: T) => boolean): QueryState<T> {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string>();
  const [status, setStatus] = useState<LoadStatus>("loading");
  const isEmptyRef = useRef(isEmpty);

  useEffect(() => {
    isEmptyRef.current = isEmpty;
  }, [isEmpty]);

  const reload = useCallback(async () => {
    setStatus("loading");
    setError(undefined);
    try {
      const nextData = await adminFetch<T>(path);
      setData(nextData);
      setStatus(isEmptyRef.current?.(nextData) ? "empty" : "ready");
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Unable to load admin data.");
      setStatus("error");
    }
  }, [path]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { data, error, reload, status };
}

export function buildAdminQuery(path: string, params: Record<string, string | undefined>) {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value && value !== "all") searchParams.set(key, value);
  }
  const query = searchParams.toString();
  return query ? `${path}?${query}` : path;
}

function buildAdminApiError(payload: unknown, status: number, headers?: Headers) {
  const error = extractErrorPayload(payload);
  const reauthorizeUrl = headers?.get("X-Shopify-API-Request-Failure-Reauthorize-Url");
  const context = { ...(error?.context ?? {}) };
  if (reauthorizeUrl) context.adminAppUrl = reauthorizeUrl;
  const message = error?.message ?? `Request failed with status ${status}`;
  const apiError = new AdminApiError(message, error?.code, context);
  maybeRedirectForAuthorization(apiError);
  return apiError;
}

function extractErrorPayload(payload: unknown): AdminErrorPayload | undefined {
  if (typeof payload !== "object" || !payload || !("error" in payload)) return undefined;
  const raw = payload.error;
  if (typeof raw === "string") return { message: raw };
  if (typeof raw !== "object" || !raw) return undefined;
  return {
    code: "code" in raw ? String(raw.code) : undefined,
    context: "context" in raw && typeof raw.context === "object" && raw.context ? (raw.context as AdminErrorContext) : {},
    message: "message" in raw ? String(raw.message) : undefined,
  };
}

function maybeRedirectForAuthorization(error: AdminApiError) {
  if (
    ![
      "SHOPIFY_AUTH_REQUIRED",
      "SHOPIFY_INSTALL_REQUIRED",
      "SHOPIFY_REINSTALL_REQUIRED",
      "SHOPIFY_TOKEN_EXCHANGE_EMPTY",
      "SHOPIFY_TOKEN_EXCHANGE_FAILED",
    ].includes(error.code ?? "")
  ) {
    return;
  }
  if (typeof window === "undefined") return;

  const redirectUrl =
    error.code === "SHOPIFY_REINSTALL_REQUIRED"
      ? error.context.authUrl ?? error.context.adminAppUrl
      : error.context.adminAppUrl ?? error.context.authUrl;
  if (!redirectUrl) return;

  const redirectKey = `whatstar:auth-redirect:${error.code}:${redirectUrl}`;
  const lastRedirectAt = Number(window.sessionStorage.getItem(redirectKey) ?? 0);
  if (Date.now() - lastRedirectAt < 10_000) return;

  window.sessionStorage.setItem(redirectKey, String(Date.now()));
  window.open(redirectUrl, "_top");
}

async function getShopifyIdToken() {
  if (typeof window === "undefined") return undefined;

  const context = getShopifyContext();
  if (window.shopify?.idToken) {
    await window.shopify.ready?.catch?.(() => undefined);
    return window.shopify.idToken();
  }

  if (context.idToken) {
    scrubShopifyIdTokenFromUrl();
    return context.idToken;
  }
  if (!context.isEmbedded) return undefined;

  const shopify = await waitForShopifyGlobal();
  await shopify?.ready?.catch?.(() => undefined);
  return shopify?.idToken?.();
}

async function waitForShopifyGlobal() {
  if (window.shopify?.idToken) return window.shopify;

  const startedAt = Date.now();
  while (Date.now() - startedAt < 5_000) {
    await new Promise((resolve) => window.setTimeout(resolve, 50));
    if (window.shopify?.idToken) return window.shopify;
  }

  return window.shopify;
}

function withShopifyContext(path: string) {
  if (typeof window === "undefined") return path;
  const contextSearch = getPreservedShopifySearch();
  if (!contextSearch) return path;

  const url = new URL(path, window.location.origin);
  const contextParams = new URLSearchParams(contextSearch);
  for (const [key, value] of contextParams) {
    if (!url.searchParams.has(key)) url.searchParams.set(key, value);
  }

  return `${url.pathname}${url.search}${url.hash}`;
}

export function getPreservedShopifySearch(search = typeof window === "undefined" ? "" : window.location.search) {
  const current = new URLSearchParams(search);
  const next = new URLSearchParams();
  for (const key of ["shop", "host", "embedded"]) {
    const value = current.get(key);
    if (value) next.set(key, value);
  }
  const query = next.toString();
  return query ? `?${query}` : "";
}

function getShopifyContext() {
  const params = new URLSearchParams(window.location.search);
  return {
    idToken: params.get("id_token") ?? undefined,
    isEmbedded:
      params.has("shop") ||
      params.has("host") ||
      params.get("embedded") === "1" ||
      params.has("id_token") ||
      isInsideIframe(),
  };
}

function scrubShopifyIdTokenFromUrl() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has("id_token")) return;

  url.searchParams.delete("id_token");
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
}

function isInsideIframe() {
  try {
    return window.top !== window.self;
  } catch {
    return true;
  }
}
